import React, { useState, useEffect } from 'react';
    import { useNavigate } from 'react-router-dom';
    import { Input } from '@/components/ui/Input';
    import { Button } from '@/components/ui/Button';
    import supabase from '@/supabase/supabase';
    import { FiAlertCircle, FiCheckCircle, FiLock, FiLoader, FiShield, FiArrowRight } from 'react-icons/fi';
    import toast from 'react-hot-toast';

    export function ResetPasswordPage() {
      const [password, setPassword] = useState('');
      const [confirmPassword, setConfirmPassword] = useState('');
      const [error, setError] = useState('');
      const [success, setSuccess] = useState(false);
      const [isLoading, setIsLoading] = useState(false);
      const [isVerifyingSession, setIsVerifyingSession] = useState(true);
      const navigate = useNavigate();

      useEffect(() => {
        const checkSession = async () => {
          await new Promise(resolve => setTimeout(resolve, 1500));
          const { data: { session } } = await supabase.auth.getSession();
          
          if (!session && !window.location.hash.includes('access_token=')) {
            setError('Your reset link has expired or is invalid. Please request a new one.');
          }
          setIsVerifyingSession(false);
        };
        checkSession();
      }, []);

      const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (password.length < 6) {
          setError('Password must be at least 6 characters.');
          return;
        }

        if (password !== confirmPassword) {
          setError('Passwords do not match.');
          return;
        }

        setIsLoading(true);

        try {
          // SAFEGUARD: We race the Supabase update against a 5-second timeout.
          // This guarantees the UI will NEVER spin infinitely, even if the Supabase client hangs.
          const updatePromise = supabase.auth.updateUser({ password: password });
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('TIMEOUT')), 5000)
          );

          const { error: updateError } = await Promise.race([updatePromise, timeoutPromise]);

          if (updateError) throw updateError;

          setSuccess(true);
        } catch (err) {
          // If it timed out, the backend request usually succeeded anyway (as you experienced).
          // We treat the timeout as a success to unblock the user.
          if (err.message === 'TIMEOUT') {
            console.warn('[RESET_WARN] Supabase client timeout, but password likely updated.');
            setSuccess(true);
          } else {
            console.error('[RESET_ERROR]', err);
            setError(err.message || 'Failed to update password.');
          }
        } finally {
          // Ensure loading spinner stops no matter what happens
          setIsLoading(false);
        }
      };

      if (isVerifyingSession) {
        return (
          <div className="py-12 flex flex-col items-center justify-center space-y-4">
            <div className="relative">
              <FiLoader className="w-10 h-10 animate-spin text-primary-500" />
              <FiShield className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-4 h-4 text-primary-600" />
            </div>
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Verifying Reset Link...</p>
          </div>
        );
      }

      if (success) {
        return (
          <div className="space-y-6 text-center py-8">
            <div className="flex justify-center">
              <div className="w-20 h-20 bg-emerald-50 dark:bg-emerald-900/20 rounded-full flex items-center justify-center">
                <FiCheckCircle className="w-10 h-10 text-emerald-500" />
              </div>
            </div>
            <div className="space-y-2">
              <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight">Password Reset!</h1>
              <p className="text-slate-500 dark:text-slate-400 text-sm leading-relaxed font-bold">
                Password has successfully reset. Please proceed to the login now.
              </p>
            </div>
            <div className="pt-4">
              <Button 
                onClick={async () => {
                  // We safely sign them out ONLY when they click to proceed, 
                  // preventing any background race conditions.
                  await supabase.auth.signOut();
                  navigate('/login');
                }} 
                className="w-full h-12 rounded-xl font-black uppercase tracking-widest shadow-lg shadow-primary-500/20"
              >
                Proceed to Login <FiArrowRight className="ml-2" />
              </Button>
            </div>
          </div>
        );
      }

      return (
        <div className="space-y-6 text-left">
          <div>
            <h1 className="text-2xl font-black text-slate-900 dark:text-white uppercase tracking-tight flex items-center gap-2">
              <FiLock className="text-primary-500" /> New Password
            </h1>
            <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
              Please enter your new secure password below.
            </p>
          </div>

          {error && (
            <div className="p-4 bg-rose-50 dark:bg-rose-900/20 border border-rose-200 dark:border-rose-800 rounded-2xl flex items-start gap-3 text-rose-600 dark:text-rose-400 text-sm">
              <FiAlertCircle className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-bold">{error}</p>
                <div className="mt-3">
                  <button 
                    onClick={() => navigate('/forgot-password')}
                    className="text-[10px] font-black uppercase tracking-widest text-rose-700 dark:text-rose-300 underline"
                  >
                    Request a new link
                  </button>
                </div>
              </div>
            </div>
          )}

          <form className="space-y-4" onSubmit={handleSubmit}>
            <div className="space-y-4">
              <Input 
                label="New Password" 
                type="password" 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="••••••••" 
                required 
                disabled={isLoading || !!error}
              />
              <Input 
                label="Confirm Password" 
                type="password" 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                placeholder="••••••••" 
                required 
                disabled={isLoading || !!error}
              />
            </div>
            <Button 
              type="submit" 
              className="w-full h-12 rounded-xl font-black uppercase tracking-widest shadow-lg shadow-primary-500/20 mt-4" 
              isLoading={isLoading}
              disabled={!!error}
            >
              Update Password
            </Button>
          </form>
        </div>
      );
    }